// skipcq: JS-W1028, JS-0128
import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { styleNeedsTypeMapping } from '../../utils/beerStyle/styleNeedsTypeMapping';

/**
 * StyleRow renders a single beer style table row.
 */
function StyleRow({ style, onEditClick }) {
  const handleEditClick = useCallback(() => {
    onEditClick(style);
  }, [style, onEditClick]);

  const needsMapping = styleNeedsTypeMapping(style);

  return (
    <TableRow hover sx={needsMapping ? { backgroundColor: 'warning.light' } : undefined}>
      <TableCell
        sx={{
          width: 200,
          maxWidth: 200,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {style.id}
      </TableCell>
      <TableCell>
        {style.name || ''}
        {needsMapping ? (
          <Chip
            label="Needs type"
            size="small"
            color="warning"
            sx={{ ml: 1 }}
          />
        ) : null}
      </TableCell>
      <TableCell>{style.type || ''}</TableCell>
      <TableCell>
        <IconButton
          size="small"
          onClick={handleEditClick}
          aria-label="edit"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

StyleRow.propTypes = {
  style: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    typeId: PropTypes.string,
    needsTypeMapping: PropTypes.bool,
  }).isRequired,
  onEditClick: PropTypes.func.isRequired,
};

/**
 * BeerStylesTable renders the table of beer styles.
 * Can display either alphabetically or grouped by type.
 */
function BeerStylesTable({ beerStyles, groupByType, onEditClick }) {
  const groupedStyles = useMemo(() => {
    if (!groupByType) {
      return null;
    }

    const grouped = {};
    beerStyles.forEach((style) => {
      const type = styleNeedsTypeMapping(style)
        ? 'Needs type mapping'
        : (style.type || 'Uncategorized');
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(style);
    });

    // Sort types alphabetically and styles within each type by name
    const sortedTypes = Object.keys(grouped).sort((left, right) => {
      if (left === 'Needs type mapping') {
        return -1;
      }
      if (right === 'Needs type mapping') {
        return 1;
      }
      return left.localeCompare(right);
    });
    const result = {};
    sortedTypes.forEach((type) => {
      result[type] = grouped[type].sort((left, right) => {
        const nameA = (left.name || '').toLowerCase();
        const nameB = (right.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    });

    return result;
  }, [beerStyles, groupByType]);

  if (groupByType && groupedStyles) {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 200, maxWidth: 200 }}>ID</TableCell>
              <TableCell>Style</TableCell>
              <TableCell>Type</TableCell>
              <TableCell sx={{ width: 80 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(groupedStyles).map(([type, styles]) => (
              <React.Fragment key={type}>
                <TableRow>
                  <TableCell
                    colSpan={4}
                    sx={{
                      backgroundColor: 'action.hover',
                      fontWeight: 'bold',
                      pt: 1.5,
                      pb: 1.5,
                    }}
                  >
                    {type}
                  </TableCell>
                </TableRow>
                {styles.map((style) => (
                  <StyleRow key={style.id} style={style} onEditClick={onEditClick} />
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 200, maxWidth: 200 }}>ID</TableCell>
            <TableCell>Style</TableCell>
            <TableCell>Type</TableCell>
            <TableCell sx={{ width: 80 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {beerStyles.map((style) => (
            <StyleRow key={style.id} style={style} onEditClick={onEditClick} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

BeerStylesTable.propTypes = {
  beerStyles: PropTypes.arrayOf(PropTypes.object).isRequired,
  groupByType: PropTypes.bool,
  onEditClick: PropTypes.func.isRequired,
};

BeerStylesTable.defaultProps = {
  groupByType: false,
};

export default BeerStylesTable;
